import type { Probleem } from './typen.ts';

/**
 * Een fout van de API. De API antwoordt volgens RFC 9457 met een titel, een omschrijving, een correlatie-id
 * en bij ongeldige invoer per veld wat er mis is.
 */
export class ApiFout extends Error {
  readonly status: number;
  readonly probleem: Probleem | null;

  /**
   * @param status De HTTP-status.
   * @param probleem De foutmelding van de API, als die er een gaf.
   */
  constructor(status: number, probleem: Probleem | null) {
    super(probleem?.detail ?? standaardmelding(status));
    this.name = 'ApiFout';
    this.status = status;
    this.probleem = probleem;
  }

  /**
   * De code van de fout, zoals `niet_aangemeld` of `csrf_ongeldig`.
   */
  get code(): string | undefined {
    return this.probleem?.code;
  }

  /**
   * De fouten per veld, met het pad als sleutel. Het pad is bijvoorbeeld `email` of `wensen.urenMin`.
   */
  get veldfouten(): Record<string, string> {
    const resultaat: Record<string, string> = {};
    for (const probleem of this.probleem?.problemen ?? []) {
      if (!(probleem.pad in resultaat)) {
        resultaat[probleem.pad] = probleem.melding;
      }
    }
    return resultaat;
  }

  /**
   * De melding voor de gebruiker, met de veldfouten erin als ze niet aan een veld gekoppeld worden.
   */
  get volledigeMelding(): string {
    const velden = Object.entries(this.veldfouten);
    if (velden.length === 0) {
      return this.message;
    }
    return `${this.message} ${velden.map(([pad, melding]) => `${pad}: ${melding}`).join('; ')}`;
  }
}

function standaardmelding(status: number): string {
  switch (status) {
    case 0:
      return 'De server is niet bereikbaar.';
    case 401:
      return 'Je bent niet aangemeld.';
    case 403:
      return 'Je hebt hier geen toestemming voor.';
    case 404:
      return 'Niet gevonden.';
    case 409:
      return 'Dit botst met bestaande gegevens.';
    case 413:
      return 'Het bestand of verzoek is te groot.';
    case 422:
      return 'De gegevens voldoen niet aan de regels.';
    case 429:
      return 'Te veel verzoeken. Probeer het over een minuut opnieuw.';
    default:
      return status >= 500 ? 'Er ging iets mis op de server.' : `Het verzoek is mislukt (${status}).`;
  }
}

/**
 * Herkent een foutmelding van de API in de body van een antwoord.
 * @param waarde De geparseerde body.
 * @returns `true` als het een probleem volgens RFC 9457 is.
 */
export function isProbleem(waarde: unknown): waarde is Probleem {
  return (
    typeof waarde === 'object' &&
    waarde !== null &&
    'status' in waarde &&
    typeof waarde.status === 'number' &&
    'detail' in waarde &&
    typeof waarde.detail === 'string'
  );
}

/**
 * Geeft een melding voor de gebruiker bij elke soort fout.
 * @param fout De opgevangen fout.
 * @returns De melding.
 */
export function foutmelding(fout: unknown): string {
  if (fout instanceof ApiFout) {
    return fout.message;
  }
  if (fout instanceof TypeError) {
    return 'De server is niet bereikbaar.';
  }
  return fout instanceof Error && fout.message ? fout.message : 'Er ging iets mis.';
}
